import { Injectable } from '@angular/core';

import { createDigestClient } from '@plugins/client/utils';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';
import { IdentityPublic } from '@plugins/userdetails-lib/entity';

import type {
  ApiEntityTypes,
  ApiSearchRequestDto,
  EntityCacheCheckReturn,
  SearchCache,
  UpdateCacheAndReturnNew,
} from '../../private/utils/types.private';
import { stringifyRequest } from '../../private/utils/utils';

/** Manages:
- **entityCache:** the central cache which collects received users / groups data from server requests.
- **searchCache:** collects ids corresponding to a specific search term and search type.
- **missingEntities:** collects a list of ids that were not returned as part of the server response.

The service offers a method for pre-populating the entityCache.

The service's methods and observables are called / subscribed to by the `UserDetailsService`. */
@Injectable({ providedIn: 'root' })
export class EntitiesCacheManagementService {
  private entityCache = new Map<string, IdentityPublic>();
  private missingEntities = new Set<string>();
  private _searchCache = new Map<string, string[]>();

  get searchCache(): SearchCache {
    return this._searchCache;
  }

  getEntityFromCache(id: string): IdentityPublic | undefined {
    return this.entityCache.get(id);
  }
  checkEntityCache(ids: string[]): EntityCacheCheckReturn {
    const notCached: string[] = [],
      existing: IdentityPublic[] = [],
      missing: string[] = [];

    ids.forEach((id) => {
      const query = this.entityCache.get(id);
      if (query) return existing.push(query);

      const cachedMissing = this.missingEntities.has(id);
      if (cachedMissing) return missing.push(id);

      return notCached.push(id);
    });
    return { existing, missing, notCached };
  }

  updateEntityCacheAndReturn(
    notCached: string[],
    receivedEntities: IdentityPublic[],
  ): UpdateCacheAndReturnNew {
    const receivedIds = receivedEntities.map((entity) => entity.id);
    const newlyMissing = notCached.filter(
      (id) => !(receivedIds.includes(id) || this.missingEntities.has(id)),
    );
    const newEntities = receivedEntities.filter(
      (entity) =>
        !(newlyMissing.includes(entity.id) || this.entityCache.get(entity.id)),
    );
    if (newlyMissing.length) {
      newlyMissing.forEach((id) => this.missingEntities.add(id));
    }

    if (newEntities.length) {
      newEntities.forEach((entity) => {
        this.entityCache.set(entity.id, entity);
      });
    }

    return {
      newEntities,
      newlyMissing,
    };
  }

  async checkSearchCache(
    searchQuery: ApiSearchRequestDto,
    type: ApiEntityTypes,
  ): Promise<string[]> {
    const hash = await this.createDigest(searchQuery, type);

    const termCache = this._searchCache.get(hash);
    if (!termCache) return [];
    return termCache;
  }

  async updateSearchCache(
    searchQuery: ApiSearchRequestDto,
    entities: IdentityPublic[],
    type: ApiEntityTypes,
  ): Promise<void> {
    const isInCache = await this.checkSearchCache(searchQuery, type);
    if (isInCache.length) return;
    const ids = entities.map((entity) => entity.id);
    const hash = await this.createDigest(searchQuery, type);
    this._searchCache.set(hash, ids);

    // Update entity cache
    const entityCacheStatus = this.checkEntityCache(ids);
    const { notCached } = entityCacheStatus;
    if (notCached.length) {
      const notCachedEntities = entities.filter((entity) =>
        notCached.includes(entity.id),
      );
      this.updateEntityCacheAndReturn(notCached, notCachedEntities);
    }
  }

  prePopulateCache(entities: IdentityPublic[]): void {
    const existingIds = [...this.entityCache.keys()];
    const newEntities = entities.filter(
      (entity) => !existingIds.includes(entity.id),
    );
    newEntities.forEach((entity) => this.entityCache.set(entity.id, entity));
  }

  get hasCrypto(): boolean {
    // window.crypto.subtle is only available in secure contexts, so we have to use
    // optional access. See: https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return !!window.crypto?.subtle;
  }

  async createDigest(
    searchQuery: ApiSearchRequestDto,
    type: ApiEntityTypes,
  ): Promise<string> {
    if (!this.hasCrypto) {
      return stringifyRequest(searchQuery, type);
    }
    const { search, includedGroups: initialIncludedGroups } = searchQuery;
    const groups = returnAsArray(initialIncludedGroups ?? []);
    // Any array values must be pre-sorted so that order difference does not result in a different string.
    // We need to create a copy of the groups, since the passed groups array
    // might be frozen.
    const includedGroups = groups.slice().sort();
    const queryDetails = {
      type,
      search,
      includedGroups,
    };

    return await createDigestClient(JSON.stringify(queryDetails));
  }
}
